// Admin Etsy listing push — P5 wire shapes + helpers
// (server: app/api/v1/etsy_listing_setup.py, services/etsy_publish_admin.py).

import { useQuery } from '@tanstack/react-query'

import { apiUrl } from '../../../lib/api'
import type { PublishStep } from '../../seller/etsy/etsy-publish.lib'

export type ResultFilter = 'attention' | 'all' | 'sent' | 'partial' | 'uncertain' | 'failed' | 'running'

export type PushRow = {
  id: string
  created_at: string
  updated_at: string | null
  status: string
  org_name: string | null
  shop_name: string | null
  store_integration_id: string | null
  listing_id: string | null
  listing_title: string | null
  external_listing_id: string | null
  steps: Record<string, string>
  is_v2: boolean
  last_error: string | null
}

export type PushList = {
  items: PushRow[]
  total: number
  counts: { total: number; sent: number; attention: number; failed: number }
}

type PushEvent = {
  at: string
  type: string
  actor_name: string | null
  detail: Record<string, unknown>
}

export type PushDetail = PushRow & {
  step_results: Record<string, PublishStep & { raw?: string | null }>
  edit_url: string | null
  summary: {
    rows: number
    on_sale: number
    price_min: string | null
    price_max: string | null
    photos: number
    tags: number
    personalization: number
  } | null
  sent_by: string | null
  duration_seconds: number | null
  can_retry: boolean
  events: PushEvent[]
}

export type GoLive = {
  code_live: boolean
  test_stores_on: string[]
  end_to_end: { job_id: string; listing_title: string | null; shop_name: string | null } | null
  confirmed_at: string | null
  confirmed_by_name: string | null
  seller_stores_on: string[]
  test_store_ids: string[]
}

export type PushFilters = {
  result: ResultFilter
  store: string
  days: number | null
  q: string
}

export const DEFAULT_FILTERS: PushFilters = { result: 'attention', store: '', days: 7, q: '' }

export const pushesKey = (f: PushFilters) => ['admin', 'etsy-listing', 'pushes', f] as const
export const pushKey = (id: string) => ['admin', 'etsy-listing', 'push', id] as const
export const GO_LIVE_KEY = ['admin', 'etsy-listing', 'go-live'] as const

export function pushesPath(f: PushFilters): string {
  const p = new URLSearchParams({ result: f.result })
  if (f.store) p.set('store_integration_id', f.store)
  if (f.days) p.set('days', String(f.days))
  if (f.q.trim()) p.set('q', f.q.trim())
  return `/admin/etsy-listing/pushes?${p.toString()}`
}

/** One mini dot per pipeline step: ok | warn | bad | none (not run). */
export function dotState(status: string | undefined): 'ok' | 'warn' | 'bad' | 'none' {
  if (!status) return 'none'
  if (status === 'ok' || status === 'skipped') return 'ok'
  if (status === 'warn' || status === 'uncertain') return 'warn'
  return 'bad'
}

export function badgeVariant(status: string): 'success' | 'warning' | 'destructive' | 'neutral' {
  if (status === 'sent') return 'success'
  if (status === 'partial' || status === 'uncertain') return 'warning'
  if (status === 'failed' || status === 'dead_letter') return 'destructive'
  return 'neutral'
}

export async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path))
  if (!res.ok) throw new Error(`${path} ${res.status}`)
  return (await res.json()) as T
}

export async function postJson<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path), { method: 'POST' })
  if (!res.ok) {
    let message = `${res.status}`
    try {
      const data = (await res.json()) as { detail?: { message?: string } }
      message = data.detail?.message ?? message
    } catch {
      // keep the status code
    }
    throw new Error(message)
  }
  return (await res.json()) as T
}

export function useGoLive() {
  return useQuery({
    queryKey: GO_LIVE_KEY,
    queryFn: () => getJson<GoLive>('/admin/etsy-listing/go-live'),
  })
}
